/**
 * Currency conversion + psychological pricing helpers.
 *
 * Supported currencies: Top 6 (USD, EUR, GBP, CHF, CAD, AUD).
 * Unsupported countries fall back to USD.
 *
 * Rates are USD-base (1 USD = rate[CURRENCY]) fetched from the Go API.
 */

import { FALLBACK_USD_RATES } from "@/lib/fx-fallback-rates";

// ZAR is a DISPLAY-only currency for the South-Africa PPP funnel (the visitor
// sees ZAR but is charged USD). It is intentionally NOT in SUPPORTED_CURRENCIES
// — that array is the currency-switcher source, and ZAR must never appear in the
// switcher (it's server-geo-gated to South Africa only) nor be sent as a charge
// currency.
export type SupportedCurrency = "USD" | "EUR" | "GBP" | "CHF" | "CAD" | "AUD" | "ZAR";

export const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
  "USD", "EUR", "GBP", "CHF", "CAD", "AUD",
];

export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  USD: "$", EUR: "€", GBP: "£", CHF: "CHF ", CAD: "CA$", AUD: "A$", ZAR: "R",
};

// Locale for number formatting (German uses comma decimal, English uses dot).
// CAD/AUD use en-US so their symbol renders as "CA$"/"A$" rather than a bare
// "$" (which en-CA/en-AU produce and is ambiguous with USD). ZAR uses en-ZA → "R".
const LOCALES: Record<SupportedCurrency, string> = {
  USD: "en-US", EUR: "de-DE", GBP: "en-GB",
  CHF: "de-CH", CAD: "en-US", AUD: "en-US", ZAR: "en-ZA",
};

export interface CurrencyData {
  country: string;
  currency: SupportedCurrency;
  base: string; // always "USD"
  rates: Record<string, number>;
  supported: string[];
}

/**
 * Convert a USD amount to target currency with psychological rounding.
 *
 * Psychological rules (mirrors common pricing psychology):
 * - Amount >= 10: round DOWN to nearest whole, then tweak last digit
 *   to match the source's "pricing style":
 *     - If source ended in 7 (e.g. $17, $47, $97, $197) → end in 7
 *     - If source ended in 9 → end in 9
 *     - If source ended in .97 / .99 → add those decimals back
 *     - Otherwise round to nearest integer
 * - Amount < 10: round to .99 for whole-ish inputs, else 2 decimals.
 */
export function convertPsychological(
  usdCents: number,
  toCurrency: SupportedCurrency,
  rates: Record<string, number>
): number {
  // Free is free in every currency — without this, $0.00 falls into the
  // "round whole-ish amounts to .99" branch and renders as 0.99.
  if (usdCents === 0) return 0;
  if (toCurrency === "USD") return usdCents;
  const rate = rates[toCurrency];
  if (!rate || rate <= 0) return usdCents;

  const sourceDollars = usdCents / 100;
  const convertedDollars = sourceDollars * rate;

  // Detect source pricing style
  const sourceStr = sourceDollars.toFixed(2);
  const [sourceWhole, sourceDec] = sourceStr.split(".");
  const sourceLastDigit = parseInt(sourceWhole.slice(-1), 10);

  let finalDollars: number;

  if (convertedDollars < 10) {
    // Small: round to 2 decimals, prefer .99 if source was whole
    if (sourceDec === "00") {
      finalDollars = Math.round(convertedDollars) - 0.01; // xx.99
      if (finalDollars < 1) finalDollars = Math.max(0.99, Math.round(convertedDollars * 100) / 100);
    } else {
      finalDollars = Math.round(convertedDollars * 100) / 100;
    }
  } else if (sourceDec === "97" || sourceDec === "99" || sourceDec === "95") {
    // Preserve decimals like .97 / .99 / .95
    const whole = Math.round(convertedDollars);
    finalDollars = whole - 1 + parseFloat("0." + sourceDec);
  } else if (sourceDec === "00" && (sourceLastDigit === 7 || sourceLastDigit === 9)) {
    // Whole-number with psychological last digit (7/9)
    const rounded = Math.round(convertedDollars);
    const lastDigit = rounded % 10;
    const diff = ((sourceLastDigit - lastDigit) + 10) % 10;
    finalDollars = diff <= 5 ? rounded + diff : rounded - (10 - diff);
  } else {
    // Default: round to nearest integer for amounts >= 10
    finalDollars = Math.round(convertedDollars);
  }

  return Math.round(finalDollars * 100);
}

/**
 * Format a cents amount in a specific currency using locale conventions.
 * Amounts that are whole (xx.00) are shown without decimals for readability.
 */
export function formatPrice(
  cents: number,
  currency: SupportedCurrency,
  opts?: { alwaysDecimals?: boolean }
): string {
  const locale = LOCALES[currency] || "en-US";
  const dollars = cents / 100;
  const isWhole = Math.abs(dollars - Math.round(dollars)) < 0.005;
  const showDecimals = opts?.alwaysDecimals || !isWhole;

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    }).format(dollars);
  } catch {
    const sym = CURRENCY_SYMBOLS[currency] || currency + " ";
    return sym + (showDecimals ? dollars.toFixed(2) : Math.round(dollars).toString());
  }
}

/**
 * Convert + format in one call.
 */
export function convertAndFormat(
  usdCents: number,
  toCurrency: SupportedCurrency,
  rates: Record<string, number>
): string {
  const converted = convertPsychological(usdCents, toCurrency, rates);
  return formatPrice(converted, toCurrency);
}

/** How long a cached `_currency_data` may price a cart. Its rates travel as
 *  `fx_rate` and the server accepts a client figure only within 2% of its own
 *  rate, so a tab left open for weeks must not keep pricing at an old rate
 *  (the page would show A$9 and the server would charge A$19). */
const CURRENCY_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/**
 * Client-side fetch of the visitor's currency from the Go API.
 * Cached in sessionStorage for up to CURRENCY_CACHE_MAX_AGE_MS. The payload
 * keeps its shape under `_currency_data` (installments-eligibility reads it);
 * the write time sits beside it in `_currency_data_at`.
 */
export async function fetchCurrencyData(): Promise<CurrencyData> {
  // Session cache: avoid re-fetching on every page, but never past its age.
  if (typeof window !== "undefined") {
    try {
      const cached = sessionStorage.getItem("_currency_data");
      const at = Number(sessionStorage.getItem("_currency_data_at"));
      if (cached && at > 0 && Date.now() - at < CURRENCY_CACHE_MAX_AGE_MS) {
        return JSON.parse(cached);
      }
    } catch { /* fall through to a fresh fetch */ }
  }

  // Use the Vercel-hosted proxy (which forwards X-Vercel-IP-Country to Go).
  // Going directly to Go would lose the country header (cross-origin).
  try {
    const res = await fetch("/api/geo/currency");
    if (!res.ok) throw new Error("geo fetch failed");
    const json = await res.json();
    const data = json.data as CurrencyData;

    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem("_currency_data", JSON.stringify(data));
        sessionStorage.setItem("_currency_data_at", String(Date.now()));
      } catch { /* storage blocked: next call fetches again */ }
    }
    return data;
  } catch {
    // Fallback: USD with no conversion. ZAR (display-only) must be present —
    // without a rate, convertPsychological returns the RAW USD cents and an SA
    // surface would print "ZAR 17.00" for a $17 amount. Keep in sync with the
    // server fallback in services/api/internal/pricing/rates.go (check:fallback-rates).
    return {
      country: "",
      currency: "USD",
      base: "USD",
      rates: { ...FALLBACK_USD_RATES },
      supported: SUPPORTED_CURRENCIES,
    };
  }
}

/**
 * User-override: stored in localStorage, persists across sessions.
 * Returns the overridden currency or null if user hasn't chosen.
 */
export function getCurrencyOverride(): SupportedCurrency | null {
  if (typeof window === "undefined") return null;
  const c = localStorage.getItem("_currency_override");
  if (c && SUPPORTED_CURRENCIES.includes(c as SupportedCurrency)) {
    return c as SupportedCurrency;
  }
  return null;
}

export function setCurrencyOverride(currency: SupportedCurrency) {
  if (typeof window === "undefined") return;
  localStorage.setItem("_currency_override", currency);
}

/**
 * Get the active currency (override if set, else geo-detected).
 */
export function getActiveCurrency(data: CurrencyData): SupportedCurrency {
  return getCurrencyOverride() || (data.currency as SupportedCurrency);
}
